// Category names and the uploaded rules must match the backend: common/constants.py
// FundingRequestAttachmentCategories and factoring/services/document_review_validation.py.
// A BOL, a Rate Confirmation and a TONU all only count once they have finished
// uploading, exactly as the backend validator treats them.
//
// Which BOL is required depends on the fuel advance (HP-9251). A fuel advance is paid against
// pickup, before any delivery has happened, so the pickup BOL is the document that exists at
// submission time. The backend expresses this as
// `required_bol = has_pickup_bol if is_fuel_advance else has_delivered_bol`.
export const DELIVERED_BOL_CATEGORY = "Signed Bill of Lading (Delivered)";
export const PICKUP_BOL_CATEGORY = "Signed Bill of Lading (Pick-Up)";
export const RATE_CONFIRMATION_CATEGORY = "Rate Confirmation";
export const TONU_CATEGORY = "Truck Order Not Used";

export interface IDocumentReviewAttachment {
  category?: unknown;
  uploaded?: unknown;
}

// Matching is case sensitive on purpose: the backend validator uses a plain `in` substring test, so
// lowercasing here would enable the button for a differently cased category that submission rejects.
const normalizeCategory = (category: unknown): string => {
  if (Array.isArray(category)) return category.join(",");
  return String(category ?? "");
};

const hasCategory = (attachments: IDocumentReviewAttachment[], category: string): boolean =>
  (attachments ?? []).some((attachment) => {
    if (!normalizeCategory(attachment?.category).includes(category)) return false;
    return Boolean(attachment?.uploaded);
  });

const requiredBolCategory = (isFuelAdvance: boolean): string =>
  isFuelAdvance ? PICKUP_BOL_CATEGORY : DELIVERED_BOL_CATEGORY;

export function getMissingDocumentReviewLabels(
  attachments: IDocumentReviewAttachment[],
  isFuelAdvance = false
): string[] {
  if (hasCategory(attachments, TONU_CATEGORY)) return [];

  const bolCategory = requiredBolCategory(isFuelAdvance);
  const missing: string[] = [];
  if (!hasCategory(attachments, bolCategory)) missing.push(bolCategory);
  if (!hasCategory(attachments, RATE_CONFIRMATION_CATEGORY)) {
    missing.push(RATE_CONFIRMATION_CATEGORY);
  }
  return missing;
}

export function hasRequiredDocumentReviewDocuments(
  attachments: IDocumentReviewAttachment[],
  isFuelAdvance = false
): boolean {
  return getMissingDocumentReviewLabels(attachments, isFuelAdvance).length === 0;
}

export function getDocumentReviewTooltipMessage(
  attachments: IDocumentReviewAttachment[],
  isFuelAdvance = false
): string {
  const missing = getMissingDocumentReviewLabels(attachments, isFuelAdvance);
  if (!missing.length) {
    return isFuelAdvance
      ? "Upload a Signed BOL from Pick-Up and a Rate Confirmation, or a TONU, to submit."
      : "Upload a Signed BOL from Delivery and a Rate Confirmation, or a TONU, to submit.";
  }
  return `Upload ${missing.join(" and ")} to submit, or a TONU instead.`;
}
